import { Action, ApiInfoPerType, Json, NodeType, StoreStructure } from '../types/types';
import { mergeCollections } from '../utils/object';
import { generateActionTypesDictionaryForStore } from './actionTypes';
import { del, parseXpath, patch, set } from './lodash';
import { toSingularAction } from './singular';
import { analyzeTransient } from './transient';

export const handleSingle =
  (nodeName: string) =>
  (state: Json = {}, action: Action) => {
    switch (action.type) {
      case `SET_${nodeName.toUpperCase()}`:
        return { ...action.payload };
      case `PATCH_${nodeName.toUpperCase()}`:
        return { ...state, ...action.payload };
      default:
        return state ?? {};
    }
  };

export const handleCollection = (nodeName: string) => {
  const item = (state: Json = {}, action: Action) => {
    switch (action.type) {
      case `ADD_${toSingularAction(nodeName)}_ITEM`:
        return { ...action.payload };
      case `SET_${toSingularAction(nodeName)}_ITEM`:
        return { ...action.payload };
      case `PATCH_${toSingularAction(nodeName)}_ITEM`:
        return {
          ...state,
          ...action.payload,
        };
      default:
        return state;
    }
  };

  const items = (state: Json = {}, action: Action) => {
    let newState;

    const id = action.id ?? action.payload?.id;

    switch (action.type) {
      case `SET_${nodeName.toUpperCase()}`:
        return action.payload;
      case `MERGE_${nodeName.toUpperCase()}`:
        return mergeCollections(state, action.payload);
      case `ADD_${toSingularAction(nodeName)}_ITEM`:
      case `SET_${toSingularAction(nodeName)}_ITEM`:
      case `PATCH_${toSingularAction(nodeName)}_ITEM`:
        if (!id) {
          return state;
        }

        return {
          ...state,
          [id]: item(state[id], action),
        };
      case `DELETE_${toSingularAction(nodeName)}_ITEM`:
        if (!id) {
          return state;
        }
        newState = { ...state };
        delete newState[id];
        return newState;

      default:
        return state ?? {};
    }
  };

  return items;
};

export const handleTransient =
  (nodeName: string, transient: any, allInfo: ApiInfoPerType) =>
  (state: Json = {}, action: Action) => {
    const xpathRaw = transient[nodeName];
    const xpath = parseXpath(xpathRaw, state.currentIds);
    const info = allInfo[action.type];

    if (info.nodeName !== nodeName) {
      return state;
    }

    const { payload = {} } = action;
    const { verb, nodeType } = info;

    switch (verb) {
      case 'get':
        break;
      case 'setAll':
        set(state, xpath, payload);
        break;
      case 'set':
        if (nodeType === NodeType.COLLECTION_NODE) { xpath.push(action.id) } // prettier-ignore
        set(state, xpath, payload);
        break;
      case 'add':
        if (nodeType === NodeType.COLLECTION_NODE) { xpath.push(payload.id) } // prettier-ignore
        set(state, xpath, payload);
        break;
      case 'patch':
        if (nodeType === NodeType.COLLECTION_NODE) { xpath.push(action.id) } // prettier-ignore
        patch(state, xpath, payload);
        break;
      case 'delete':
        if (nodeType === NodeType.COLLECTION_NODE) { xpath.push(action.id) } // prettier-ignore
        del(state, xpath);
        break;
    }

    return state;
  };

export const generateReducerForStore = <T extends StoreStructure>(storeState: T) => {
  const transient = analyzeTransient<T>(storeState);
  const transientKeys = Object.keys(transient);
  const allInfo = generateActionTypesDictionaryForStore<T>(storeState);

  return (state: T = storeState, action: Action) => {
    const { type } = action;

    if (type?.startsWith('@@redux')) {
      return clearNodes(state, transientKeys);
    }

    const info = allInfo[type];
    if (!info) {
      return clearNodes(state, transientKeys);
    }

    const { nodeType, nodeName } = info;

    const isTransient = Object.keys(transient).includes(nodeName);

    let output = { ...state } as any;

    if (!isTransient) {
      const handler = nodeType === NodeType.SINGLE_NODE ? handleSingle(nodeName) : handleCollection(nodeName); // prettier-ignore
      output[nodeName] = handler(state[nodeName], action);
    } else {
      output = handleTransient(nodeName, transient, allInfo)(state, action);
    }

    return clearNodes(output, transientKeys);
  };
};

export const clearNodes = <T extends StoreStructure>(state: T, nodes: string[]): T => {
  nodes.forEach((node) => {
    delete state[node];
  });

  return state;
};
